/**
 * USMapSVG Component
 * 
 * Main component that renders the interactive US map with all states.
 * Handles hover interactions, click navigation, and tooltip display.
 */

import React, { useState } from 'react';
import { STATE_PATHS } from '../data/statePaths.js';
import { STATE_CONFIG } from '../data/stateConfig.js';
import {
  getStateStatus,
  getStateColor,
  getTooltipMessage,
  getNavigationURL,
  isStateClickable
} from '../utils/stateClassifier.js';
import { Tooltip } from './Tooltip.jsx';
import { Legend } from './Legend.jsx';
import { StateInset } from './StateInset.jsx';
import '../styles/map.css';

export function USMapSVG() {
  const [hoveredState, setHoveredState] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [touchTimeout, setTouchTimeout] = useState(null);

  const handleStateHover = (stateCode, event) => {
    setHoveredState(stateCode);
    setTooltipPosition({ x: event.clientX, y: event.clientY });
  };

  const handleStateMove = (event) => {
    if (hoveredState) {
      setTooltipPosition({ x: event.clientX, y: event.clientY });
    }
  };

  const handleStateLeave = () => {
    setHoveredState(null);
    if (touchTimeout) {
      clearTimeout(touchTimeout);
      setTouchTimeout(null);
    }
  };

  const handleStateClick = (stateCode) => {
    const url = getNavigationURL(stateCode);
    if (url) {
      // Use absolute URL, so it works in iframe or standalone
      window.top.location.href = url;
    }
  };

  const handleKeyPress = (event, stateCode) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleStateClick(stateCode);
    }
  };

  // Touch event handlers for mobile
  const handleTouchStart = (stateCode, event) => {
    event.preventDefault();
    const touch = event.touches[0];
    setHoveredState(stateCode);
    setTooltipPosition({ x: touch.clientX, y: touch.clientY });

    // For active states, show tooltip for 2 seconds before navigating
    if (isStateClickable(stateCode)) {
      const timeout = setTimeout(() => {
        handleStateClick(stateCode);
      }, 2000);
      setTouchTimeout(timeout);
    }
  };

  const handleTouchEnd = (event) => {
    event.preventDefault();
    // Clear tooltip after a short delay
    setTimeout(() => {
      setHoveredState(null);
    }, 500);
    
    if (touchTimeout) {
      clearTimeout(touchTimeout);
      setTouchTimeout(null);
    }
  };

  return (
    <div className="interactive-us-map">
      <div className="map-container">
        <svg
          viewBox="0 0 959 593"
          xmlns="http://www.w3.org/2000/svg"
          role="img"
          aria-label="Interactive US Map showing lottery data availability"
        >
          {Object.entries(STATE_PATHS).map(([stateCode, pathData]) => {
            const status = getStateStatus(stateCode);
            const isHovered = hoveredState === stateCode;
            const color = getStateColor(status, isHovered);
            const clickable = isStateClickable(stateCode);
            const stateName = STATE_CONFIG[stateCode]?.name || stateCode;
            const tooltipMsg = getTooltipMessage(stateCode);

            return (
              <g key={stateCode}>
                {/* Invisible larger hit area for better mobile touch targets */}
                <path
                  d={pathData}
                  fill="transparent"
                  stroke="transparent"
                  strokeWidth="25"
                  className={`state-touch-target ${clickable ? 'clickable' : ''}`}
                  onMouseEnter={(e) => handleStateHover(stateCode, e)}
                  onMouseMove={handleStateMove}
                  onMouseLeave={handleStateLeave}
                  onClick={() => handleStateClick(stateCode)}
                  onKeyPress={(e) => handleKeyPress(e, stateCode)}
                  onTouchStart={(e) => handleTouchStart(stateCode, e)}
                  onTouchEnd={handleTouchEnd}
                  style={{
                    cursor: clickable ? 'pointer' : 'default',
                    pointerEvents: 'all'
                  }}
                  aria-hidden="true"
                />
                {/* Visible state path */}
                <path
                  d={pathData}
                  fill={color}
                  stroke="#ffffff"
                  strokeWidth="1"
                  className="state-path"
                  style={{
                    transition: 'fill 0.2s ease',
                    pointerEvents: 'none'
                  }}
                  aria-label={`${stateName} - ${tooltipMsg}`}
                  role={clickable ? 'link' : 'img'}
                  tabIndex={clickable ? 0 : -1}
                />
              </g>
            );
          })}
        </svg>

        <StateInset
          onStateHover={handleStateHover}
          onStateLeave={handleStateLeave}
          onStateClick={handleStateClick}
          hoveredState={hoveredState}
        />
      </div>

      {hoveredState && (
        <Tooltip
          message={getTooltipMessage(hoveredState)}
          position={tooltipPosition}
        />
      )}

      <Legend />
    </div>
  );
}
